import classNames from 'classnames';
import React, { useState } from 'react';
import {
  CircleCheckIcons,
  CircleXIcons,
} from '../../../../../../../../icons/icons';
import {
  CheckCircle,
  ChevronDown,
  ChevronUp,
  XCircle,
} from '../../../../../../../../ui/icons/icons';
import { getDownloadLink } from '../../../../../../../../utils/api_service/assessment_api';
import { convertSecondsToTimeTaken } from '../../../../../../../../utils/helpers/time';
import { AssessmentQuestionResult } from '../../../../../../../../utils/types/assessment_types';
import { SkillQuestionAndAnswer } from './SkillQuestionAndAnswer';

type QuestionCollapsibleProps = {
  assessmentQuestionResult: AssessmentQuestionResult;
  updateDetailedQuestionArray: (
    questionArray: AssessmentQuestionResult,
    getLinkStatus?: boolean
  ) => void;
  showSystemAnswer?: boolean;
  showVideo?: boolean;
};

export const QuestionCollapsible = ({
  assessmentQuestionResult,
  updateDetailedQuestionArray,
  showSystemAnswer = false,
  showVideo = false,
}: QuestionCollapsibleProps) => {
  const [resultOpen, setResultOpen] = useState<boolean>(false);

  const getVideoLink = () => {
    if (!assessmentQuestionResult.videoUrl) {
      getDownloadLink(assessmentQuestionResult.id).then((response) => {
        assessmentQuestionResult.videoUrl = response.url;
        updateDetailedQuestionArray(assessmentQuestionResult, true);
      });
    }
  };

  return (
    <>
      <div
          className={classNames(
            'rounded-none border transition-all duration-300',
            resultOpen
              ? 'bg-cyan-300/40 border-gray-100 shadow-[0_4px_20px_-5px_rgba(0,0,0,0.1)]'
              : 'bg-blue-200/20 border-gray-100 hover:shadow-[0_8px_25px_-10px_rgba(0,0,0,0.1)] hover:-translate-y-0.5',
          )}
      >
        <div className='p-2'>
          <div className='flex justify-between items-start gap-4'>
            { /* Status Icon */ }
            { /* Content */ }
            <div className='flex items-center gap-2 min-w-0'>
              <div
                  className={classNames(
                    'flex-shrink-0 w-8 h-8 rounded-none border flex items-center justify-center',
                    assessmentQuestionResult.isCorrect
                      ? 'bg-green-50/50 border-green-100/50'
                      : 'bg-red-50/50 border-red-100/50',
                  )}
              >
                { assessmentQuestionResult.isCorrect ? (
                  <CircleCheckIcons className='w-3.5 h-3.5 text-green-500' />
                ) : (
                  <CircleXIcons className='w-3.5 h-3.5 text-red-500' />
                ) }
              </div>
              <p className='text-gray-900 text-[13px] leading-relaxed '>
                { assessmentQuestionResult.systemQuestion }
              </p>

              { /* Metrics */ }
              <div className='flex items-center gap-4 text-sm'>
                { /* Score */ }
                <div className='flex items-center gap-2 px-1.5 py-0.5 bg-gray-50/30 border border-gray-100 rounded-none'>
                  <span className='w-1.5 h-1.5 rounded-none bg-blue-500/80'></span>
                  <span className='text-[10px] font-bold text-gray-400 uppercase tracking-widest'>
                    { assessmentQuestionResult.score || 0 } points
                  </span>
                </div>

                { /* Time */ }
                <div className='flex items-center gap-2 px-1.5 py-0.5 bg-gray-50/30 border border-gray-100 rounded-none'>
                  <span className='w-1.5 h-1.5 rounded-none bg-purple-500/80'></span>
                  <span className='text-[10px] font-bold text-gray-400 uppercase tracking-widest'>
                    { assessmentQuestionResult.timeExpired
                      ? 'Time Expired'
                      : convertSecondsToTimeTaken(
                        assessmentQuestionResult.timeTaken,
                      ) || '0:00' }
                  </span>
                </div>
              </div>
              { /* Expand Button */ }
            </div>{ ' ' }
            { assessmentQuestionResult.userAnswer !== undefined && (
              <button
                  onClick={() => {
                    setResultOpen(!resultOpen);
                    getVideoLink();
                  }}
                  className={classNames(
                    'ml-auto flex items-center gap-2 px-3 py-1.5 border rounded-none text-[10px] font-bold uppercase tracking-widest transition-all duration-300',
                    resultOpen
                      ? 'bg-blue-600 text-white border-blue-600 shadow-lg'
                      : 'bg-white text-gray-400 border-gray-200 hover:text-blue-600 hover:border-blue-200 hover:bg-blue-50/30',
                  )}
              >
                { resultOpen ? (
                  <>
                    <span>Hide Answer</span>
                    <ChevronUp />
                  </>
                ) : (
                  <>
                    <span>Show Answer</span>
                    <ChevronDown />
                  </>
                ) }
              </button>
            ) }
          </div>
        </div>
        { resultOpen && assessmentQuestionResult.userAnswer !== undefined && (
          <div className='border-t border-gray-100'>
            <div className='p-4 bg-white'>
              <SkillQuestionAndAnswer
                  key={assessmentQuestionResult.id}
                  assessmentQuestionResult={assessmentQuestionResult}
                  updateDetailedQuestionArray={updateDetailedQuestionArray}
                  setResultOpen={setResultOpen}
                  showSystemAnswer={showSystemAnswer}
                  showVideo={showVideo}
              />
            </div>
          </div>
        ) }
      </div>
    </>
  );
};
